import React, {Suspense} from "react"
import {createRoot} from "react-dom/client"
import {Provider, useDispatch} from "react-redux"
import {Route, Router, Switch} from "react-router-dom"
import {Portal} from "scholastic-client-components/src/types/securityTypes"
import {useUpdateMessage} from "scholastic-client-components/src/hooks/useUpdateMessage"
import {notificationListSlice} from "scholastic-client-components/src/slices/notificationListSlice"
import {MobileProvider} from "scholastic-client-components/src/features/MobileContextComponent/MobileContextComponent"
import {formErrorMiddleware} from "scholastic-client-components/src/middlewares/formErrorMiddleware"
import {authSlice} from "scholastic-client-components/src/slices/authSlice"
import {axiosInstance} from "scholastic-client-components/src/api/authAPI"
import {BottomRightCornerBox} from "scholastic-client-components/src/features/BottomRightCornerBox/BottomRightCornerBox"
import {BugsnagErrorBoundaryWrapper} from "scholastic-client-components/src/middlewares/bugsnagErrorBoundaryMiddleware"
import {Notifications} from "./features/Notifications"
import {useAuth} from "./hooks/useAuth"
import {useCanvaHeaders} from "./hooks/useCanvaHeaders"
import {AppPaths, history} from "./lib/routing"
import {trialEditSlice} from "./slices/trialEditSlice"
import {store} from "./store/store"
import "./stylesheets/index.scss"
import {useLocation} from "react-router-dom"
import {AuthWrap} from "scholastic-client-components/src/features/AuthWrap/AuthWrap"
import {I18nWrapper} from "scholastic-client-components/src/i18n/I18nWrapper"
import {App} from "./App"

const PrivacyPage = React.lazy(() => import("./Pages/PrivacyPage"))
const LazyPrivacyPage = () => (
    <Suspense fallback={null}>
        <PrivacyPage />
    </Suspense>
)

const TermsPage = React.lazy(() => import("./Pages/TermsPage"))
const LazyTermsPage = () => (
    <Suspense fallback={null}>
        <TermsPage />
    </Suspense>
)

const DataDeletionPage = React.lazy(() => import("./Pages/DataDeletionPage"))
const LazyDataDeletionPage = () => (
    <Suspense fallback={null}>
        <DataDeletionPage />
    </Suspense>
)

const configureAxios = () => {
    axiosInstance.defaults.baseURL = window.location.origin
    axiosInstance.defaults.headers.common["Portal-Type"] = Portal.Home
    axiosInstance.interceptors.response.use(
        (response) => response,
        (error) => {
            if (error.response.status >= 500) {
                store.dispatch(
                    notificationListSlice.actions.setNotification({
                        title: error.response.statusText,
                        message: error.message,
                        data: error.response.data.message,
                    })
                )
            } else if (error.response.status === 422) {
                const trialEdit = error.response.config.url.includes("api/trial/edit")
                const trialCheckDevice = error.response.config.url.includes("api/trial/checkDevice")
                const confirmAccount = error.response.config.url.includes("api/user/confirmAccount")
                if (trialEdit) {
                    store.dispatch(
                        trialEditSlice.actions.setError(error.response.data.errors._error)
                    )
                } else if (trialCheckDevice) {
                    return Promise.resolve({
                        ...error.response,
                        data: {
                            error: error.response.data.errors._error,
                        },
                    })
                } else if (confirmAccount) {
                    return Promise.reject(error)
                } else {
                    store.dispatch(
                        authSlice.actions.setRequestTOSAgreement({requestTOSAgreement: false})
                    )
                    store.dispatch(formErrorMiddleware.action(error.response.data))
                }
            } else if (error.response.status === 403) {
                const nonConfirmedAccount = error.response.headers["non-confirmed"]
                const request2fa = error.response.headers["request-tfa"]
                const requestTOSAgreement = error.response.headers["request-tos-agreement"]
                const SSOLink = error.response.headers["sso-link"]
                const resendTFA = error.response.config.url.includes("api/user/resendTFA")

                if (nonConfirmedAccount || request2fa || requestTOSAgreement || SSOLink) {
                    return Promise.resolve({
                        ...error.response,
                        data: {
                            request2fa: request2fa === "true",
                            nonConfirmedAccount: nonConfirmedAccount === "true",
                            requestTOSAgreement: requestTOSAgreement === "true",
                            SSOLink,
                        },
                    })
                }
                if (resendTFA) {
                    store.dispatch(authSlice.actions.reset())
                    return Promise.resolve(error.response)
                }
            } else if (error.response.status === 429) {
                const retryAfterHeader = error.response.headers["retry-after"]
                const retryAfter = (retryAfterHeader || retryAfterHeader === "0" || retryAfterHeader === 0) ? Number(retryAfterHeader) : 60
                return Promise.reject({...error, retryAfter})
            }
            return Promise.reject(error.response)
        }
    )
}

const ns = ["glossary", "common", "auth_portal", "person"]

const AppWrapper: React.FC = () => {
    const dispatch = useDispatch()
    const canvaHeaders = useCanvaHeaders()
    const canvaLogin = !!canvaHeaders["canva-id"]
    const {pathname} = useLocation()

    const turnOffAuth =
        canvaLogin ||
        pathname.includes(AppPaths.ResetPassword) ||
        pathname.includes(AppPaths.LoginSSO) ||
        pathname.includes(AppPaths.SSOFailure)

    const auth = useAuth({turnOff: turnOffAuth})

    useUpdateMessage(dispatch, "/auth/update.html")

    const onPageWithMandatoryAuth = pathname === AppPaths.Device
    const ignoreAuthStatus = !onPageWithMandatoryAuth || turnOffAuth

    return (
        <AuthWrap {...auth} ignoreAuthStatus={ignoreAuthStatus}>
            <I18nWrapper ns={ns}>
                <MobileProvider>
                    <Route path="/" component={App} />
                    <BottomRightCornerBox notificationNode={<Notifications />} />
                </MobileProvider>
            </I18nWrapper>
        </AuthWrap>
    )
}

const container = document.getElementById("scholastic-root")
const root = createRoot(container!)
root.render(
    <BugsnagErrorBoundaryWrapper>
        <Provider store={store}>
            <Router history={history}>
                <Switch>
                    <Route path={AppPaths.Privacy} component={LazyPrivacyPage} />
                    <Route path={AppPaths.Terms} component={LazyTermsPage} />
                    <Route path={AppPaths.DataDeletion} component={LazyDataDeletionPage} />
                    <Route path="/" component={AppWrapper} />
                </Switch>
            </Router>
        </Provider>
    </BugsnagErrorBoundaryWrapper>
)

configureAxios()
